/* ==========================================================================
   Burly — cart-drawer.js
   AJAX cart: add / update / remove line items without page reloads, then
   re-render the drawer (and header cart count) via Shopify's Section
   Rendering API so Liquid stays the single source of truth for markup.

   Public API (used by product forms in later stages):
     Burly.cart.add(formData)      — POST /cart/add.js, then open drawer
     Burly.cart.change(line, qty)  — POST /cart/change.js
     Burly.cart.open() / close()

   META PIXEL HOOK: an `AddToCart` event should be fired where marked below.
   ========================================================================== */

(function () {
  'use strict';

  window.Burly = window.Burly || {};

  const SECTION_ID = 'cart-drawer';

  const drawerEl = () => document.getElementById('CartDrawer');
  const overlayEl = () => document.getElementById('PageOverlay');

  /* ---- Fetch helpers ---------------------------------------------------- */
  function jsonHeaders() {
    return { 'Content-Type': 'application/json', Accept: 'application/json' };
  }

  async function refreshDrawer() {
    // Section Rendering API: fetch just the drawer's HTML and swap it in.
    const response = await fetch(window.location.pathname + '?sections=' + SECTION_ID);
    const sections = await response.json();
    const html = sections[SECTION_ID];
    if (!html) return;

    const template = document.createElement('div');
    template.innerHTML = html;

    const freshInner = template.querySelector('[data-cart-drawer-inner]');
    const currentInner = document.querySelector('[data-cart-drawer-inner]');
    if (freshInner && currentInner) {
      currentInner.replaceWith(freshInner);
    }

    // Sync every cart-count bubble (header desktop + mobile).
    const freshCount = template.querySelector('[data-cart-count]');
    if (freshCount) {
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.textContent = freshCount.textContent;
        el.classList.toggle('is-empty', freshCount.textContent.trim() === '0');
        // Micro-interaction: pulse the bubble when the count changes.
        el.classList.remove('cart-count-pulse');
        void el.offsetWidth; // restart animation
        el.classList.add('cart-count-pulse');
      });
    }
  }

  /* ---- Open / close ------------------------------------------------------ */
  function open() {
    const drawer = drawerEl();
    if (!drawer) return;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    if (overlayEl()) overlayEl().classList.add('is-active');
    document.body.classList.add('overlay-open');

    const closeButton = drawer.querySelector('[data-cart-close]');
    if (closeButton) closeButton.focus();
    document.addEventListener('keydown', onEscape);
  }

  function close() {
    const drawer = drawerEl();
    if (!drawer) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    if (overlayEl()) overlayEl().classList.remove('is-active');
    document.body.classList.remove('overlay-open');
    document.removeEventListener('keydown', onEscape);
  }

  function onEscape(event) {
    if (event.key === 'Escape') close();
  }

  /* ---- Cart mutations ------------------------------------------------------ */
  async function add(body) {
    const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      body
    });
    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.description || result.message || 'Add to cart failed');
    }

    /* --------------------------------------------------------------------
       META PIXEL HOOK — AddToCart
       Insert pixel call here (or listen for the event below from a custom
       pixel / theme.liquid script):
         fbq('track', 'AddToCart', { content_ids: [result.id], ... })
       -------------------------------------------------------------------- */
    Burly.events.emit('cart:item-added', result);

    await refreshDrawer();
    open();
    return result;
  }

  async function change(line, quantity) {
    const drawer = drawerEl();
    if (drawer) drawer.classList.add('is-loading');

    const response = await fetch(window.Shopify.routes.root + 'cart/change.js', {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify({ line, quantity })
    });
    const cart = await response.json();

    await refreshDrawer();
    if (drawer) drawer.classList.remove('is-loading');
    Burly.events.emit('cart:updated', cart);
    return cart;
  }

  async function updateNote(note) {
    await fetch(window.Shopify.routes.root + 'cart/update.js', {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify({ note })
    });
  }

  Burly.cart = { add, change, open, close, refresh: refreshDrawer, updateNote };

  /* ---- Event delegation (survives drawer re-renders) ------------------------ */
  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-cart-toggle]');
    if (toggle) {
      // Only hijack the cart link when drawer mode is on.
      if (document.body.dataset.cartType === 'drawer') {
        event.preventDefault();
        open();
      }
      return;
    }

    if (event.target.closest('[data-cart-close]')) {
      event.preventDefault();
      close();
      return;
    }

    const removeLink = event.target.closest('[data-cart-remove]');
    if (removeLink) {
      event.preventDefault();
      change(parseInt(removeLink.dataset.line, 10), 0);
    }
  });

  document.addEventListener('change', (event) => {
    const qtyInput = event.target.closest('[data-cart-qty]');
    if (qtyInput) {
      change(parseInt(qtyInput.dataset.line, 10), parseInt(qtyInput.value, 10) || 0);
      return;
    }

    const note = event.target.closest('[data-cart-note]');
    if (note) updateNote(note.value);
  });

  // Clicking the shared page overlay closes whatever is open.
  document.addEventListener('click', (event) => {
    if (event.target.id === 'PageOverlay') {
      close();
      Burly.events.emit('overlay:clicked');
    }
  });
})();
